import * as Actions from '@/redux/actions';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useDialogs } from '@toolpad/core';
import StatusChip from 'components/status-chip';
import Table from 'components/table';
import TableRow from 'components/table/TableRow';
import { useDispatch, useListParams, useSelector } from 'hooks';
import { dateTimeToString, dateToString, localizeFund, TranslatedText } from 'lib/utils';
import { useI18n } from 'locales';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import thousands from 'thousands';

const selector = (state) => ({
  subscriptionInfo: state.subscriptionInfo,
});

function SubscriptionDetailsDialog({ open, onClose, subscriptionId }) {
  const dispatch = useDispatch();
  const t = useI18n();
  const dialogs = useDialogs();
  const listParams = useListParams();
  const { subscriptionInfo } = useSelector(selector, shallowEqual);

  const headCells = useMemo(() => [
    {
      id: 'date',
      type: 'datetime',
      label: t('Date'),
      width: 120,
      render: ({ row }) => dateTimeToString(row.date) || '-',
    },
    {
      id: 'amount',
      type: 'number',
      label: t('Amount'),
      width: 120,
      render: ({ row }) => (
        <Typography color='primary' variant='h6'>
          {'HKD ' + thousands((row.amount || 0).toFixed(2))}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: t('Status'),
      width: 120,
      render: ({ row }) => <StatusChip status={row.status} />,
    },
    {
      id: 'refId',
      label: t('Ref ID'),
      width: 150,
      render: ({ row }) => <code>{row.refId || '-'}</code>,
    },
  ], [t]);

  useEffect(() => {
    if (subscriptionId && open) {
      dispatch(Actions.readSubscription({
        payload: { id: subscriptionId },
      }));
    }
  }, [subscriptionId, open, dispatch]);

  useEffect(() => {
    if (subscriptionId && open) {
      dispatch(Actions.readSubscription({
        payload: { id: subscriptionId },
      }));
    }
  }, [subscriptionId, open, dispatch]);

  const onClickUnsubscribeHandler = useCallback(async () => {
    const confirmed = await dialogs.confirm(
      t('Are you sure you want to unsubscribe this PCG instruction?'),
      {
        title: t('Unsubscribe'),
        okText: t('Unsubscribe'),
        cancelText: t('Do Not Unsubscribe'),
        severity: 'error',
      },
    );

    if (confirmed) {
      dispatch(Actions.cancelSubscription({ payload: { id: subscriptionId } }));
      onClose();
    }
  }, [dispatch, dialogs, t, subscriptionId, onClose]);

  const subscription = subscriptionInfo.data || {};
  const givings = get(subscription, 'givings', []);
  const lastGiving = givings.length > 0 ? givings[givings.length - 1] : null;
  const isFetching = subscriptionInfo.isFetching;

  const givingsInfo = useMemo(() => ({
    data: {
      items: givings,
      current_page: 1,
      items_per_page: givings.length,
      total_item_count: givings.length,
    },
    isFetching: false,
  }), [givings]);

  const renderRow = useCallback(({ isItemSelected, labelId, row, handleClick }, index) => {
    return (
      <TableRow
        key={`table-row-${index}`}
        headCells={headCells}
        row={row}
        isItemSelected={isItemSelected}
        labelId={labelId}
        onSelect={handleClick}
      />
    );
  }, [headCells]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={isFetching ? undefined : onClose}
    >
      <DialogTitle>{t('Subscription Details')}</DialogTitle>
      <DialogContent>
        {isFetching && <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress /></Stack>}

        {!isFetching && subscription.id && (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Top Section */}
            <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Type')}
                  </Typography>
                  <Typography variant="h6">
                    <TranslatedText value={lastGiving?.type || '-'} />
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Planned Amount')}
                  </Typography>
                  <Typography variant="h6">
                    <strong>HKD {lastGiving ? thousands(lastGiving.amount.toFixed(2)) : '-'}</strong>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Last Payment Date')}
                  </Typography>
                  <Typography variant="h6">
                    {dateToString(lastGiving?.date) || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Details Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('Details')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Planned Status')}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <StatusChip status={subscription.status === 'Enabled' ? 'Active' : subscription.status === 'Disabled' ? 'Cancelled' : subscription.status} />
                    {subscription.status === 'Enabled' && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={onClickUnsubscribeHandler}
                      >
                        {t('Unsubscribe')}
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Payment Method')}
                  </Typography>
                  <Typography variant="body1">
                    {lastGiving?.onlinePayment?.paymentMethod || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Start Date')}
                  </Typography>
                  <Typography variant="body1">
                    {dateToString(subscription.startTime) || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Ref ID')}
                  </Typography>
                  <Typography variant="body1">
                    <code>{subscription.refId || '-'}</code>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Fund')}
                  </Typography>
                  <Typography variant="body1">
                    {lastGiving?.fund ? localizeFund(lastGiving.fund, 'en') : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('Next Billing Date')}
                  </Typography>
                  <Typography variant="body1">
                    {dateToString(subscription.nextBillingTime) || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Past Givings Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('Past Givings')}
              </Typography>
              {givings.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('No past givings found')}
                  </Typography>
                </Box>
              ) : (
                <Table
                  modelName="Giving"
                  params={listParams}
                  info={givingsInfo}
                  headCells={headCells}
                  renderRow={renderRow}
                  hideToolbar
                />
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SubscriptionDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscriptionId: PropTypes.string,
};

export default SubscriptionDetailsDialog;
